// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Popover,
    CircularProgress,
    IconButton,
    InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/Check';
import { useAreas } from '../State/AreasContext';

export default function SelectAreaArbol({ area, setArea, disabled = false }) {
    const { getAreas, fetchSubgrupos } = useAreas();
    const [anchorEl, setAnchorEl] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [loadingIds, setLoadingIds] = useState(new Set());
    const [loadingRootAreas, setLoadingRootAreas] = useState(false);
    const [rootAreas, setRootAreas] = useState([]);
    const anchorRef = useRef(null);

    useEffect(() => {
        setLoadingRootAreas(true);
        getAreas()
            .then(areas => setRootAreas(areas ?? []))
            .finally(() => setLoadingRootAreas(false));
    }, []);

    const selectedLabel = nodes.find(n => n.id === area)?.name ?? rootAreas.find(a => a.id === area)?.name ?? '';

    const handleOpen = () => {
        if (disabled) return;
        setAnchorEl(anchorRef.current);
        if (nodes.length > 0 || rootAreas.length === 0) return;
        setNodes(
            rootAreas
                .filter(a => a.id !== "ff73f6b1-6201-4193-81dc-ae35d7dbe235") // Filtro "Grupo VPN" para PRD.
                .map(a => ({ ...a, level: 0, expanded: false, loaded: false }))
        );
    };

    const handleExpand = async (e, node) => {
        e.stopPropagation();
        if (node.expanded) {
            setNodes(prev => collapse(prev, node.id));
            return;
        }
        if (node.loaded) {
            setNodes(prev => prev.map(n => n.id === node.id ? { ...n, expanded: true } : n));
            return;
        }
        setLoadingIds(prev => new Set(prev).add(node.id));
        try {
            const subgrupos = await fetchSubgrupos(node);
            const children = subgrupos.map(s => ({
                ...s,
                level: node.level + 1,
                expanded: false,
                loaded: false,
                parentId: node.id,
            }));
            setNodes(prev => {
                const idx = prev.findIndex(n => n.id === node.id);
                const next = [...prev];
                next[idx] = { ...next[idx], expanded: true, loaded: true };
                next.splice(idx + 1, 0, ...children);
                return next;
            });
        } finally {
            setLoadingIds(prev => { const s = new Set(prev); s.delete(node.id); return s; });
        }
    };

    const collapse = (nodes, parentId) => {
        const childIds = nodes.filter(n => n.parentId === parentId).map(n => n.id);
        let result = nodes.map(n => n.id === parentId ? { ...n, expanded: false, loaded: false } : n);
        for (const childId of childIds) {
            result = collapse(result, childId);
        }
        return result.filter(n => n.parentId !== parentId);
    };

    const handleSelect = (node) => {
        setArea(node.id);
        setAnchorEl(null);
    };

    return (
        <Box>
            <TextField
                ref={anchorRef}
                label="Área *"
                variant="outlined"
                fullWidth
                onClick={handleOpen}
                value={selectedLabel}
                onChange={() => {}}
                disabled={disabled}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            {loadingRootAreas
                                ? <CircularProgress size={20} color="inherit" />
                                : <ExpandMoreIcon color="action" />
                            }
                        </InputAdornment>
                    ),
                }}
                sx={{ cursor: disabled ? 'default' : 'pointer', '& input': { cursor: disabled ? 'default' : 'pointer' } }}
            />

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { width: anchorRef.current?.offsetWidth ?? 400, maxHeight: 360 } }}
            >
                <Box sx={{ overflowY: 'auto', maxHeight: 360 }}>
                    {loadingRootAreas ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : nodes.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No se encontraron áreas
                        </Typography>
                    ) : (
                        nodes.map(node => (
                            <Box
                                key={node.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    pl: 1 + node.level * 2,
                                    pr: 1,
                                    py: 0.25,
                                    cursor: 'pointer',
                                    bgcolor: node.id === area ? 'action.selected' : 'transparent',
                                    '&:hover': { bgcolor: node.id === area ? 'action.selected' : 'action.hover' },
                                }}
                                onClick={() => handleSelect(node)}
                            >
                                {node.subGroupCount > 0 ? (
                                    <IconButton
                                        size="small"
                                        onClick={e => handleExpand(e, node)}
                                        sx={{ mr: 0.5 }}
                                    >
                                        {loadingIds.has(node.id)
                                            ? <CircularProgress size={16} />
                                            : node.expanded
                                                ? <ExpandMoreIcon fontSize="small" />
                                                : <ChevronRightIcon fontSize="small" />
                                        }
                                    </IconButton>
                                ) : (
                                    <Box sx={{ width: 34 }} />
                                )}
                                <Typography variant="body2" sx={{ flex: 1 }}>{node.name}</Typography>
                                {node.id === area && <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} />}
                            </Box>
                        ))
                    )}
                </Box>
            </Popover>
        </Box>
    );
}
